/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import {
  FiAlertCircle,
  FiArrowRight,
  FiCalendar,
  FiCheckCircle,
  FiInfo,
  FiLoader,
  FiMapPin,
  FiMessageSquare,
  FiUsers,
} from 'react-icons/fi';
import PackagePaxSteppers from '../common/PackagePaxSteppers';
import {
  formatDate,
  formatPrice,
  gradeLabel,
  offeredPaxTypes,
  optionLeadRate,
  seatsOf,
  totalPaxOf,
} from '../../utils/packageUtils';

/**
 * Everything the quote needs: a departure, a joining city / grade (price
 * option) and the travellers by room sharing. The estimate is per-person
 * rate × people; the quote call returns the binding total.
 *
 * Layout: head (from price) / body (the three choices, scrolls) / foot
 * (estimate + actions, always visible while the panel is sticky).
 */
function PackageBookingPanel({
  pkg,
  departures,
  options,
  selection,
  onChange,
  estimate,
  paxError,
  quoting,
  quoteError,
  onContinue,
  onEnquire,
}) {
  const option = options.find((o) => o.priceKey === selection.priceKey) || null;
  const currency = option?.currency || pkg.fare?.currency || 'INR';
  const shown = departures.filter((d) => d.reason !== 'past');
  const bookable = departures.filter((d) => d.bookable);
  const people = totalPaxOf(selection.pax);
  const depositPct = Number(pkg.depositPct) || 0;

  let blocker = '';
  if (!selection.departureKey) blocker = 'Select a departure date';
  else if (!selection.priceKey) blocker = 'Select a joining city';
  else if (paxError) blocker = paxError;
  else if (estimate == null) blocker = 'Not sold for this room sharing';

  return (
    <div className="pkg-panel" id="pkg-panel">
      <div className="pkg-panel__head">
        <small>Starting from</small>
        <strong>
          {pkg.fare?.grandTotal
            ? formatPrice(pkg.fare.grandTotal, currency)
            : '—'}
          <span> per person</span>
        </strong>
      </div>

      <div className="pkg-panel__body">
        {/* 1. departure */}
        <section className="pkg-panel__block">
          <h4>
            <FiCalendar /> 1. Departure date
          </h4>
          {shown.length === 0 && (
            <p className="pkg-panel__empty">
              No upcoming departures. Send an enquiry for other dates.
            </p>
          )}
          <div className="pkg-panel__options" role="radiogroup">
            {shown.map((d) => {
              const active = selection.departureKey === d.key;
              return (
                <button
                  type="button"
                  role="radio"
                  aria-checked={active}
                  key={d.key}
                  disabled={!d.bookable}
                  className={`pkg-option ${active ? 'pkg-option--active' : ''}`}
                  onClick={() => onChange({ departureKey: d.key })}
                >
                  <span className="pkg-option__main">
                    <strong>{formatDate(d.date, true)}</strong>
                    {d.returnDate && (
                      <small>Returns {formatDate(d.returnDate)}</small>
                    )}
                  </span>
                  <span className="pkg-option__side">
                    {d.bookable && d.seatsAvailable != null && (
                      <small
                        className={
                          d.seatsAvailable <= 5 ? 'pkg-option__low' : ''
                        }
                      >
                        {d.seatsAvailable} seat
                        {d.seatsAvailable === 1 ? '' : 's'} left
                      </small>
                    )}
                    {!d.bookable && (
                      <small className="pkg-option__low">{d.reasonText}</small>
                    )}
                    {d.guaranteed && (
                      <span className="pkg-chip pkg-chip--accent">
                        <FiCheckCircle size={11} /> Guaranteed
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
          {shown.length > 0 && bookable.length === 0 && (
            <p className="pkg-panel__empty">
              No departure has {seatsOf(selection.pax)} seats free. Reduce
              travellers or send an enquiry.
            </p>
          )}
        </section>

        {/* 2. joining city / grade */}
        <section className="pkg-panel__block">
          <h4>
            <FiMapPin /> 2. Joining city &amp; hotel grade
          </h4>
          {options.length === 0 && (
            <p className="pkg-panel__empty">
              Prices are not published yet. Send an enquiry.
            </p>
          )}
          <div className="pkg-panel__options" role="radiogroup">
            {options.map((o) => {
              const active = selection.priceKey === o.priceKey;
              const lead = optionLeadRate(o);
              return (
                <button
                  type="button"
                  role="radio"
                  aria-checked={active}
                  key={o.priceKey}
                  className={`pkg-option ${active ? 'pkg-option--active' : ''}`}
                  onClick={() => onChange({ priceKey: o.priceKey })}
                >
                  <span className="pkg-option__main">
                    <strong>{o.city || '—'}</strong>
                    <small>{gradeLabel(o.grade, pkg)}</small>
                  </span>
                  {lead != null && (
                    <span className="pkg-option__side">
                      <small>{formatPrice(lead, o.currency)} pp</small>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* 3. travellers */}
        <section className="pkg-panel__block">
          <h4>
            <FiUsers /> 3. Travellers &amp; room sharing
          </h4>
          {option ? (
            <PackagePaxSteppers
              pax={selection.pax}
              onChange={(pax) => onChange({ pax })}
              types={offeredPaxTypes(option)}
              rates={option.rates}
              currency={currency}
            />
          ) : (
            <p className="pkg-panel__empty">Select a joining city first.</p>
          )}
          {paxError && <p className="pkg-field-error">{paxError}</p>}
        </section>
      </div>

      <div className="pkg-panel__foot">
        <div className="pkg-panel__total">
          <span>
            Estimated total
            <small>
              {people} traveller{people === 1 ? '' : 's'}
            </small>
          </span>
          <strong>
            {estimate != null ? formatPrice(estimate, currency) : '—'}
          </strong>
        </div>
        {depositPct > 0 && estimate != null && (
          <p className="pkg-panel__note">
            <FiInfo />
            {depositPct}% deposit is due at booking; the balance{' '}
            {Number(pkg.balanceDueDaysBeforeTravel) > 0
              ? `${pkg.balanceDueDaysBeforeTravel} days before travel`
              : 'before travel'}
            .
          </p>
        )}

        {quoteError && (
          <div className="pkg-alert" role="alert">
            <FiAlertCircle />
            <span>{quoteError}</span>
          </div>
        )}

        <button
          type="button"
          className="pkg-panel__cta"
          onClick={onContinue}
          disabled={Boolean(blocker) || quoting}
        >
          {quoting && (
            <>
              <FiLoader className="pkg-spin" /> Checking price…
            </>
          )}
          {!quoting && blocker}
          {!quoting && !blocker && (
            <>
              Continue to Book <FiArrowRight />
            </>
          )}
        </button>
        <button
          type="button"
          className="pkg-panel__enquire"
          onClick={onEnquire}
        >
          <FiMessageSquare /> Send an enquiry
        </button>
      </div>
    </div>
  );
}

PackageBookingPanel.propTypes = {
  pkg: PropTypes.object.isRequired,
  departures: PropTypes.array.isRequired,
  options: PropTypes.array.isRequired,
  selection: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  estimate: PropTypes.number,
  paxError: PropTypes.string,
  quoting: PropTypes.bool,
  quoteError: PropTypes.string,
  onContinue: PropTypes.func.isRequired,
  onEnquire: PropTypes.func.isRequired,
};

PackageBookingPanel.defaultProps = {
  estimate: null,
  paxError: '',
  quoting: false,
  quoteError: '',
};

export default PackageBookingPanel;
